import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["type"];
import { convertJsonToFieldValueAvro } from 'rpc-client-utils';
import { noopTelemetry } from '../chatTelemetry';
import { createDeltaAccumulator } from './deltaAccumulator';
import { CONTENT_BLOCK_TYPENAME_PREFIX, ABLY_TYPE_TO_CHIRP_SUFFIX } from './ablyConstants';
function mapAblyContentBlock(block) {
  var _ABLY_TYPE_TO_CHIRP_S;
  const ablyType = block.type;
  const suffix = (_ABLY_TYPE_TO_CHIRP_S = ABLY_TYPE_TO_CHIRP_SUFFIX[ablyType]) !== null && _ABLY_TYPE_TO_CHIRP_S !== void 0 ? _ABLY_TYPE_TO_CHIRP_S : `${ablyType}ChirpContentBlock`;
  const rest = _objectWithoutPropertiesLoose(block, _excluded);
  if ((ablyType === 'TOOL_USE' || ablyType === 'SERVER_TOOL_USE') && rest.input != null && typeof rest.input === 'object' && !('__typename' in rest.input)) {
    rest.input = convertJsonToFieldValueAvro(rest.input);
  }
  return Object.assign({}, rest, {
    __typename: `${CONTENT_BLOCK_TYPENAME_PREFIX}${suffix}`
  });
}
function isAblyFullMessage(event) {
  return 'id' in event && 'role' in event && 'content' in event && !('type' in event);
}
function mapAblyFullMessage(event) {
  const content = event.content.map(mapAblyContentBlock);
  return Object.assign({}, event, {
    content
  });
}
function createDeferred() {
  let resolve;
  const promise = new Promise(resolveFn => {
    resolve = resolveFn;
  });
  return {
    promise,
    resolve: resolve
  };
}
export function createAblyTransport(connection, logger, options = {}, telemetry = noopTelemetry) {
  const {
    resumeTimeoutMs = 30000,
    cancelInterruptTimeoutMs = 5000
  } = options;
  let isProcessingTurn = false;
  let turnDeferred = null;
  let postStreamQueue = [];
  const deltaAccumulator = createDeltaAccumulator();
  let fullMessageReceived = false;
  // true when a full message has already handled (or intentionally skipped) its
  // stop reason, so MESSAGE_END should not fire resolveTurn a second time
  let fullMessageHandledStopReason = false;
  // false until the new message has been sent to the backend. Guards against
  // Ably channel rewind replaying historical events from the previous turn and
  // prematurely ending the new turn via resolveTurn.
  let acceptingTurnEvents = false;
  let cancelFallbackTimer = null;
  // Current turn's handler; kept alive across AWAITING pauses so a cancel then
  // can still drive the turn to completion. Cleared only on full teardown.
  let activeSendHandler = null;
  function clearCancelFallback() {
    if (cancelFallbackTimer !== null) {
      clearTimeout(cancelFallbackTimer);
      cancelFallbackTimer = null;
    }
  }
  async function resolveTurn(stopReason, handler) {
    clearCancelFallback();
    const mappedStopReason = stopReason === 'TOOL_USE' ? 'EXTERNAL_TOOL' : stopReason;
    isProcessingTurn = false;
    const currentDeferred = turnDeferred;
    turnDeferred = null;
    firePostStreamActions(handler);
    await handler.onStopReason(mappedStopReason);
    currentDeferred === null || currentDeferred === void 0 || currentDeferred.resolve();
  }
  function processAblyEvent(event, handler) {
    var _handler$onStreamEven;
    logger.transportStreamEvent(event);
    logger.ablyRawEvent(event);
    (_handler$onStreamEven = handler.onStreamEvent) === null || _handler$onStreamEven === void 0 || _handler$onStreamEven.call(handler, event);
    const eventType = event.type;
    if (eventType === 'MESSAGE_START') {
      fullMessageReceived = false;
      fullMessageHandledStopReason = false;
    }
    const isFullMsg = isAblyFullMessage(event);
    if (isFullMsg) {
      fullMessageReceived = true;
      deltaAccumulator.reset();
      const role = event.role;
      const stopReason = event.stopReason;
      if (role === 'ERROR') {
        var _map$join, _content;
        if (!acceptingTurnEvents) return;
        const rawText = (_map$join = (_content = event.content) === null || _content === void 0 ? void 0 : _content.map(block => block.text).join('')) !== null && _map$join !== void 0 ? _map$join : 'Unknown error';
        isProcessingTurn = false;
        const currentDeferred = turnDeferred;
        turnDeferred = null;
        firePostStreamActions(handler);
        handler.onError({
          type: 'UNKNOWN',
          message: 'Something went wrong. Please try again.',
          cause: new Error(rawText)
        });
        currentDeferred === null || currentDeferred === void 0 || currentDeferred.resolve();
        return;
      }
      const message = mapAblyFullMessage(event);
      handler.onMessageUpdate({
        type: 'append',
        message,
        stopReason: stopReason !== null && stopReason !== void 0 ? stopReason : undefined
      });
      if (stopReason && acceptingTurnEvents) {
        fullMessageHandledStopReason = true;
        if (stopReason === 'TOOL_USE') {
          var _contentBlocks$some;
          const contentBlocks = event.content;
          const hasClientToolUseBlock = (_contentBlocks$some = contentBlocks === null || contentBlocks === void 0 ? void 0 : contentBlocks.some(block => block.type === 'TOOL_USE')) !== null && _contentBlocks$some !== void 0 ? _contentBlocks$some : false;
          if (!hasClientToolUseBlock) {
            // All tool calls are server-side — channel continues automatically.
            return;
          }
        }
        void resolveTurn(stopReason, handler);
      }
      return;
    }
    if (eventType === 'MESSAGE_END') {
      const stopReason = event.stopReason;
      if (stopReason && acceptingTurnEvents) {
        if (fullMessageHandledStopReason || stopReason === 'TOOL_USE') {
          // Full message already handled (or skipped) this stop reason, or TOOL_USE
          // stop reason — wait for EXTERNAL_TOOL which the BE defers until after the
          // turn status write, ensuring sendToolResponse never races the DB update.
          return;
        }
        void resolveTurn(stopReason, handler);
      }
      return;
    }
    if (fullMessageReceived) return;
    const deltaMessage = deltaAccumulator.handleEvent(event);
    if (deltaMessage) {
      handler.onMessageUpdate({
        type: 'delta-update',
        message: deltaMessage
      });
    }
  }
  function firePostStreamActions(handler) {
    const actions = postStreamQueue.splice(0);
    for (const action of actions) {
      void action().catch(error => {
        handler.onError(error);
      });
    }
  }
  function resolveAndCleanup() {
    clearCancelFallback();
    activeSendHandler = null;
    connection.unsubscribe();
    postStreamQueue = [];
    if (turnDeferred) {
      turnDeferred.resolve();
      turnDeferred = null;
    }
  }
  const transport = {
    activeStatus: 'STREAMING',
    isPersistentChannel: true,
    async send(request, handler) {
      var _request$externalSkil;
      activeSendHandler = handler;
      isProcessingTurn = true;
      turnDeferred = createDeferred();
      acceptingTurnEvents = false;
      const chirpRequest = Object.assign({
        agentId: request.agentId
      }, request.conversationId ? {
        conversationId: request.conversationId
      } : {}, request.collectionId ? {
        collectionId: request.collectionId
      } : {}, {
        message: request.message
      }, request.turnMetadata ? {
        turnMetadata: request.turnMetadata
      } : {}, request.conversationMetadata ? {
        conversationMetadata: request.conversationMetadata
      } : {}, request.messageContexts ? {
        messageContexts: request.messageContexts
      } : {}, request.fileReferences ? {
        fileReferences: request.fileReferences
      } : {}, {
        streaming: true,
        ablyEnabled: true
      }, request.configVersion != null ? {
        configVersion: request.configVersion
      } : {}, {
        externalToolSpecs: request.externalToolSpecs,
        externalSkillSpecs: (_request$externalSkil = request.externalSkillSpecs) !== null && _request$externalSkil !== void 0 ? _request$externalSkil : []
      }, request.externalSubagentSpecs ? {
        externalSubagentSpecs: request.externalSubagentSpecs
      } : {});
      let firstEventLogged = false;
      const ablyHandler = {
        onEvent(event) {
          if (!firstEventLogged) {
            firstEventLogged = true;
            logger.ablySubscribed('first-event-received');
          }
          processAblyEvent(event, handler);
        },
        onError(error) {
          var _turnDeferred;
          logger.transportError(error);
          telemetry.streamError(error);
          handler.onError(error);
          isProcessingTurn = false;
          (_turnDeferred = turnDeferred) === null || _turnDeferred === void 0 || _turnDeferred.resolve();
          turnDeferred = null;
        }
      };
      const pendingTurn = turnDeferred.promise;
      const sendResponse = await connection.send(chirpRequest, ablyHandler, (conversationId, turnId, title) => {
        acceptingTurnEvents = true;
        handler.onTurnStarted(conversationId, turnId, title);
      });
      logger.transportSendResponse(request, sendResponse);
      await pendingTurn;
    },
    async sendToolResponse(request, handler) {
      logger.transportToolResponse(request);
      if (isProcessingTurn) {
        postStreamQueue.push(() => doSendToolResponse(request, handler));
        return;
      }
      await doSendToolResponse(request, handler);
    },
    async sendToolApproval(request, handler) {
      if (isProcessingTurn) {
        postStreamQueue.push(() => doSendToolApproval(request, handler));
        return;
      }
      await doSendToolApproval(request, handler);
    },
    async resume(request, handler) {
      var _response$conversatio, _response$conversatio2;
      logger.transportResume(request);
      activeSendHandler = handler;
      isProcessingTurn = true;
      turnDeferred = createDeferred();
      acceptingTurnEvents = true;
      let resumeTimer = null;
      function resetResumeTimeout() {
        if (resumeTimer !== null) {
          clearTimeout(resumeTimer);
        }
        resumeTimer = setTimeout(() => {
          resumeTimer = null;
          if (isProcessingTurn && turnDeferred) {
            handler.onError({
              type: 'TRANSPORT_ERROR',
              message: 'Resume timed out waiting for events'
            });
            isProcessingTurn = false;
            const currentDeferred = turnDeferred;
            turnDeferred = null;
            currentDeferred.resolve();
          }
        }, resumeTimeoutMs);
      }
      const ablyHandler = {
        onEvent(event) {
          if (resumeTimer !== null) {
            resetResumeTimeout();
          }
          processAblyEvent(event, handler);
        },
        onError(error) {
          var _turnDeferred2;
          if (resumeTimer !== null) {
            clearTimeout(resumeTimer);
            resumeTimer = null;
          }
          logger.transportError(error);
          telemetry.streamError(error);
          handler.onError(error);
          isProcessingTurn = false;
          (_turnDeferred2 = turnDeferred) === null || _turnDeferred2 === void 0 || _turnDeferred2.resolve();
          turnDeferred = null;
        }
      };
      const pendingTurn = turnDeferred.promise;
      const response = await connection.getOrCreateAblyChannel(request.conversationId, ablyHandler);
      logger.transportResumeResponse(request, {
        conversationId: (_response$conversatio = response.conversationId) !== null && _response$conversatio !== void 0 ? _response$conversatio : request.conversationId,
        ablyChannelId: response.ablyChannelId
      });
      handler.onTurnStarted((_response$conversatio2 = response.conversationId) !== null && _response$conversatio2 !== void 0 ? _response$conversatio2 : request.conversationId, request.turnId, response.title);

      // Response messages are AviatorChirpMessage (CHIRP-typed with __typename),
      // NOT raw Ably events. Emit directly, don't feed through processAblyEvent.
      const responseMessages = response.messages;
      const lastMessage = responseMessages === null || responseMessages === void 0 ? void 0 : responseMessages[responseMessages.length - 1];
      const terminalStopReason = lastMessage === null || lastMessage === void 0 ? void 0 : lastMessage.stopReason;
      if (terminalStopReason) {
        connection.unsubscribe();
        handler.onMessageUpdate({
          type: 'append',
          message: lastMessage
        });
        isProcessingTurn = false;
        const currentDeferred = turnDeferred;
        turnDeferred = null;
        void (async () => {
          await handler.onStopReason(terminalStopReason);
          currentDeferred === null || currentDeferred === void 0 || currentDeferred.resolve();
        })();
      } else {
        // Events delivered synchronously during getOrCreateAblyChannel
        // (e.g. channel rewind) may arrive before the handler pointer
        // is live — the connection drops those via the !activeHandler
        // guard. The timeout is the safety net for that case.
        resetResumeTimeout();
      }
      await pendingTurn;
      if (resumeTimer !== null) {
        clearTimeout(resumeTimer);
        resumeTimer = null;
      }
    },
    async cancelTurn(request) {
      logger.transportCancel(request);
      // Stay subscribed so the interrupt (stopReason: CANCELLED) can arrive and
      // resolve the turn. If it never does, drive completion via the fallback.
      const handlerForFallback = activeSendHandler;
      const deferredAtCancel = turnDeferred;
      clearCancelFallback();
      cancelFallbackTimer = setTimeout(() => {
        cancelFallbackTimer = null;
        // Skip if another path already terminated this turn (interrupt, ERROR
        // message, or connection error) — they null/replace turnDeferred.
        if (turnDeferred !== deferredAtCancel) return;
        resolveAndCleanup();
        void (handlerForFallback === null || handlerForFallback === void 0 ? void 0 : handlerForFallback.onStopReason('CANCELLED'));
      }, cancelInterruptTimeoutMs);
      try {
        await connection.cancelTurn({
          conversationId: request.conversationId,
          turnId: request.turnId,
          reason: request.reason
        });
      } catch (_unused) {
        // best-effort — fallback timer handles cleanup if no interrupt arrives
      }
    },
    async preconnect(conversationId) {
      if (isProcessingTurn) {
        // Don't tear down the active turn's subscription. send() will
        // re-subscribe to the correct channel after the RPC returns if needed.
        return;
      }
      await connection.preconnect(conversationId).catch(error => {
        logger.transportError(error);
        telemetry.streamError(error);
      });
    },
    stop() {
      resolveAndCleanup();
    },
    clear() {
      resolveAndCleanup();
      isProcessingTurn = false;
      acceptingTurnEvents = false;
    },
    async submitFeedback(request) {
      try {
        await connection.submitFeedback(request);
      } catch (_unused2) {
        // fire-and-forget
      }
    }
  };
  async function doSendToolResponse(request, handler) {
    activeSendHandler = handler;
    isProcessingTurn = true;
    turnDeferred = createDeferred();
    acceptingTurnEvents = true;
    const ablyHandler = {
      onEvent(event) {
        processAblyEvent(event, handler);
      },
      onError(error) {
        var _turnDeferred3;
        logger.transportError(error);
        telemetry.streamError(error);
        handler.onError(error);
        isProcessingTurn = false;
        (_turnDeferred3 = turnDeferred) === null || _turnDeferred3 === void 0 || _turnDeferred3.resolve();
        turnDeferred = null;
      }
    };
    const pendingTurn = turnDeferred.promise;
    try {
      var _request$externalSkil2;
      await connection.sendToolResponse(Object.assign({
        conversationId: request.conversationId,
        turnId: request.turnId,
        toolResults: request.toolResults,
        externalToolSpecs: request.externalToolSpecs,
        externalSkillSpecs: (_request$externalSkil2 = request.externalSkillSpecs) !== null && _request$externalSkil2 !== void 0 ? _request$externalSkil2 : []
      }, request.externalSubagentSpecs ? {
        externalSubagentSpecs: request.externalSubagentSpecs
      } : {}, request.turnMetadata && {
        turnMetadata: request.turnMetadata
      }, request.messageContexts && {
        messageContexts: request.messageContexts
      }), ablyHandler);
      logger.transportToolResponseResponse(request, {});
    } catch (err) {
      isProcessingTurn = false;
      const currentDeferred = turnDeferred;
      turnDeferred = null;
      currentDeferred === null || currentDeferred === void 0 || currentDeferred.resolve();
      throw err;
    }
    await pendingTurn;
  }
  async function doSendToolApproval(request, handler) {
    activeSendHandler = handler;
    isProcessingTurn = true;
    turnDeferred = createDeferred();
    acceptingTurnEvents = true;
    const ablyHandler = {
      onEvent(event) {
        processAblyEvent(event, handler);
      },
      onError(error) {
        var _turnDeferred4;
        logger.transportError(error);
        telemetry.streamError(error);
        handler.onError(error);
        isProcessingTurn = false;
        (_turnDeferred4 = turnDeferred) === null || _turnDeferred4 === void 0 || _turnDeferred4.resolve();
        turnDeferred = null;
      }
    };
    const pendingTurn = turnDeferred.promise;
    await connection.sendToolApproval(Object.assign({
      conversationId: request.conversationId,
      turnId: request.turnId,
      approvals: request.approvals
    }, request.turnMetadata && {
      turnMetadata: request.turnMetadata
    }, request.messageContexts && {
      messageContexts: request.messageContexts
    }), ablyHandler);
    await pendingTurn;
  }
  return transport;
}